import { ReadonlyVec2, ReadonlyVec3, vec3 } from 'gl-matrix';
import { enqueueSnackbar } from '~/utils/notifications';
import { ImageObject } from '~/engine/data/image/ImageObject';
import { RegionOfInterest } from '~/engine/data/structure/RegionOfInterest';
import {
    Orientations,
    PlaneView,
    PlaneViewType,
} from '~/engine/view/views/PlaneView';
import { View } from '../../../view/View';
import {
    EventHandlerResult,
    EventHandlerResultType,
} from '../../EventHandlerResult';
import { ModifierKeys } from '../../ModifierKeys';
import { MOUSE_BUTTON_LEFT, MOUSE_BUTTONS_NONE } from '../../MouseButton';
import { Tool } from '../../Tool';
import {
    DeformAction,
    DeformActionState,
} from '../../actions/contouring-actions/DeformAction';
import { VolumeViewUpdateEvent } from '~/engine/view/events/VolumeViewUpdateEvent';
import { LayerBit } from '~/engine/view/layers/LayerBit';

const MIN_DIAMETER_MM = 2;
const MAX_DIAMETER_MM = 50;

/**
 * {@link DeformTool} applies a 2D deformation to an existing
 * ROI's SDF. Delegates stroke state management to
 * {@link DeformAction} and {@link DeformActionState}.
 *
 * This version works directly on the SDF voxel data using
 * trilinear sampling and Gaussian distance decay, without
 * extracting polygon outlines.
 */
export class DeformTool extends Tool {
    public readonly toolType = 'deform';

    private static readonly SIZE_MODIFIER_KEYS = ModifierKeys.SHIFT;

    private readonly structure: RegionOfInterest;
    private readonly image: ImageObject;
    private _diameter: number;
    private readonly diameterChangedCallback: (diameter: number) => void;

    private _cursor?: SdfDeformCanvasCursor;
    private _deformAction?: DeformAction;
    private _deformState?: DeformActionState;
    private _activeView?: PlaneView;

    public constructor(
        image: ImageObject,
        structure: RegionOfInterest,
        diameter: number,
        diameterChangedCallback: (diameter: number) => void
    ) {
        super();
        this.image = image;
        this.structure = structure;
        this._diameter = Math.max(
            MIN_DIAMETER_MM,
            Math.min(MAX_DIAMETER_MM, diameter)
        );
        this.diameterChangedCallback = diameterChangedCallback;
    }

    public get diameter(): number {
        return this._diameter;
    }

    public set diameter(value: number) {
        const clamped = Math.max(
            MIN_DIAMETER_MM,
            Math.min(MAX_DIAMETER_MM, value)
        );
        if (clamped !== this._diameter) {
            this._diameter = clamped;
            this._cursor?.setDiameter(this._diameter);
        }
    }

    public override onMouseWheel(
        delta: 1 | -1,
        modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (modifierKeys.equals(DeformTool.SIZE_MODIFIER_KEYS)) {
            let step: number;
            if (this._diameter <= 10) {
                step = 2;
            } else if (this._diameter <= 20) {
                step = 4;
            } else if (this._diameter <= 40) {
                step = 6;
            } else {
                step = 10;
            }
            this.diameter = this._diameter + delta * step;
            this._cursor?.setDiameter(this._diameter);
            this._cursor?.draw(view, coordinates);
            this.diameterChangedCallback(this._diameter);
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onMousePress(
        button: number,
        _modifierKeys: ModifierKeys,
        coordinates: ReadonlyVec2,
        view: PlaneView
    ): EventHandlerResult {
        if (this.structure.canModify === false) {
            enqueueSnackbar(
                'Modifying approved structures is not allowed.' +
                    ' Unapprove the structure to make changes.',
                { variant: 'warning' }
            );
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        if (
            button === MOUSE_BUTTON_LEFT &&
            view.label === Orientations.TRANSVERSAL_ORIENTATION &&
            view.type === PlaneViewType.Primary
        ) {
            view.snapToImagePixel();
            this._deformAction = new DeformAction(
                this.structure,
                this.image,
                this._diameter
            );
            this._deformState = this._deformAction.startAction(
                view,
                coordinates
            );
            this._activeView = view;
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        if (this.isActionInProgress()) {
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onMouseMove(
        coordinates: ReadonlyVec2,
        buttons: number,
        view: PlaneView
    ): EventHandlerResult {
        if (buttons === MOUSE_BUTTONS_NONE && this._deformState) {
            return this.finishStroke();
        }

        if (this._deformState) {
            this._deformState.onMouseMove(coordinates);
            this._cursor?.draw(view, coordinates);
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }

        // Cursor hover
        if (
            view instanceof PlaneView &&
            view.label === Orientations.TRANSVERSAL_ORIENTATION &&
            this.structure.canModify &&
            view.type === PlaneViewType.Primary
        ) {
            if (!this._cursor) {
                this._cursor = new SdfDeformCanvasCursor(
                    this._diameter,
                    this.structure.color
                );
            }
            this._cursor.draw(view, coordinates);
            return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onMouseRelease(
        _button: number,
        _coordinates: ReadonlyVec2,
        _view: View
    ): EventHandlerResult {
        if (this._deformState) {
            return this.finishStroke();
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onKeyPress(
        key: string,
        _modifierKeys: ModifierKeys,
        _view: View
    ): EventHandlerResult {
        if (key === 'Escape' && this._deformState) {
            this._deformState.cancel();
            this._deformState = undefined;
            this._deformAction = undefined;
            this._activeView = undefined;
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_HANDLED,
                true,
                new VolumeViewUpdateEvent(
                    this.structure.boundingBox,
                    LayerBit.CONTOUR_LAYER_BIT,
                    true
                )
            );
        }
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override isActionInProgress(): boolean {
        return this._deformState !== undefined;
    }

    public override getDrawingView(): PlaneView | undefined {
        return this.isActionInProgress() ? this._activeView : undefined;
    }

    public override onActivate(): EventHandlerResult {
        this.expandToImageSize();
        return new EventHandlerResult(EventHandlerResultType.EVENT_NOT_HANDLED);
    }

    public override onDeactivate(): EventHandlerResult {
        if (this._deformState && this._deformAction) {
            const result = this._deformAction.stopAction(this._deformState);
            this._deformState = undefined;
            this._deformAction = undefined;
            this._activeView = undefined;
            this._cursor?.clear();
            this._cursor = undefined;
            return result;
        }
        this._cursor?.clear();
        this._cursor = undefined;
        return new EventHandlerResult(EventHandlerResultType.EVENT_HANDLED);
    }

    public override dispose(): void {
        this.structure.texture?.cropToContentBounds();
    }

    private finishStroke(): EventHandlerResult {
        if (!this._deformState || !this._deformAction) {
            return new EventHandlerResult(
                EventHandlerResultType.EVENT_NOT_HANDLED
            );
        }
        const result = this._deformAction.stopAction(this._deformState);
        this._deformState = undefined;
        this._deformAction = undefined;
        this._activeView = undefined;
        return result;
    }

    /** Expand the texture to cover the full image grid. */
    private expandToImageSize(): void {
        const texture = this.structure.texture;
        if (!texture || texture.isEmpty) {
            return;
        }
        texture.expandToImageGrid();
    }
}

/**
 * Lightweight cursor that draws a circle on the PlaneView's
 * 2D canvas overlay.
 */
class SdfDeformCanvasCursor {
    private diameterMm: number;
    private readonly color: ReadonlyVec3;
    private lastView?: PlaneView;

    public constructor(diameterMm: number, color: ReadonlyVec3) {
        this.diameterMm = diameterMm;
        this.color = color;
    }

    public setDiameter(diameterMm: number): void {
        this.diameterMm = diameterMm;
    }

    public draw(view: PlaneView, mouseCoords: ReadonlyVec2): void {
        if (this.lastView && this.lastView !== view) {
            this.clearCanvas(this.lastView);
        }
        this.lastView = view;

        const canvas = view.get2DCanvas;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (!ctx) {
            return;
        }

        const w = canvas.offsetWidth;
        const h = canvas.offsetHeight;
        if (ctx.canvas.width !== w || ctx.canvas.height !== h) {
            ctx.canvas.width = w;
            ctx.canvas.height = h;
        }

        ctx.clearRect(0, 0, w, h);

        const patientPos = view.clientToPatientCoordinates(mouseCoords);
        const canvasCenter = view.canvasCoordsFromPatientCoords(patientPos);

        const offsetPos = vec3.fromValues(
            patientPos[0] + this.diameterMm / 2,
            patientPos[1],
            patientPos[2]
        );
        const canvasEdge = view.canvasCoordsFromPatientCoords(offsetPos);
        const radiusPx = Math.abs(canvasEdge[0] - canvasCenter[0]);

        const r = Math.round(this.color[0] * 255);
        const g = Math.round(this.color[1] * 255);
        const b = Math.round(this.color[2] * 255);

        ctx.strokeStyle = `rgb(${r}, ${g}, ${b})`;
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.arc(canvasCenter[0], canvasCenter[1], radiusPx, 0, 2 * Math.PI);
        ctx.stroke();
    }

    public clear(): void {
        if (this.lastView) {
            this.clearCanvas(this.lastView);
            this.lastView = undefined;
        }
    }

    private clearCanvas(view: PlaneView): void {
        const canvas = view.get2DCanvas;
        if (!canvas) {
            return;
        }
        const ctx = canvas.getContext('2d');
        if (ctx) {
            ctx.clearRect(0, 0, ctx.canvas.width, ctx.canvas.height);
        }
    }
}
